import { devlog, devwarn } from '../../logic/utils.ts';
import '../base/buttons/as-text-button';
import '../base/buttons/as-icon-button';
import styleText from './as-popout-button.css?raw';

/**
 * # ASPopoutButton Component
 * 
 * A button component that toggles a popout card when clicked.
 * 
 * ## Features
 * - Supports both text and icon buttons
 * - Uses as-text-button or as-icon-button for consistent button styling
 * - Automatically creates and manages an as-popout-card
 * - Places children in the popout card's slot
 * - Configurable button text via the `default-text` attribute (for text buttons)
 * - Configurable localized text via the `dict-key` attribute (for text buttons)
 * - Configurable icon via the `icon-name` or `file-name` attributes (for icon buttons)
 * - Controls popout card placement via the `placement` attribute
 * - Can be shown by default via the `show` attribute
 * - Automatically disables button tooltips when the popout is open to prevent overlap
 * 
 * ## Component Hierarchy
 * ```
 * ASPopoutButton (as-popout-button)
 * └── as-text-button or as-icon-button - The clickable button element
 * └── as-popout-card - The popout card (created internally)
 *     └── slot - For the popout card content (user's children)
 * ```
 * 
 * ## Attributes
 * - `button-type`: Type of button to use ("text" or "icon", default: "text")
 * - `default-text`: Sets the text displayed on the button when button-type="text" (default: "Toggle")
 * - `dict-key`: Sets the localized text key when button-type="text"
 * - `icon-name`: Sets the icon name when button-type="icon"
 * - `file-name`: Sets the icon file name when button-type="icon"
 * - `placement`: Controls where the popout card appears relative to the button (default: "right")
 *   - Supported values: "right", "left", "top", "bottom", "bottom-left", "bottom-right", "top-left", "top-right"
 * - `show`: When present, shows the popout card by default
 * - `tooltip-default-text`: Default text content for the tooltip
 * - `tooltip-dict-key`: Localization key for tooltip text
 * - `tooltip-placement`: Position of the tooltip relative to the button (default: "bottom")
 *   - Supported values: "top", "right", "bottom", "left", "bottom-left", "bottom-right", "top-left", "top-right"
 * 
 * ## Events
 * ### Emitted Events
 * - `toggle-visibility`: Dispatched to the associated popout card when the button is clicked
 * 
 * ### Internal Behavior
 * - When the popout opens, the button's tooltips are automatically disabled via the `disable-tooltips` attribute
 * - When the popout closes, the button's tooltips are re-enabled by removing the `disable-tooltips` attribute
 */
export class ASPopoutButton extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  private popoutCard: Element | null = null;
  private buttonElement: HTMLElement | null = null;
  private buttonText: string = "Toggle";
  private buttonType: string = "text";
  private textKey: string | null = null;
  private iconName: string | null = null;
  private file_name: string | null = null;
  private placement: string = "right";
  private cardSlot: HTMLSlotElement | null = null;
  private tooltipDefaultText: string | null = null;
  private tooltipDictKey: string | null = null;
  private tooltipPlacement: string = "bottom";

  /**
   * Define the observed attributes for the component
   */
  static get observedAttributes(): string[] {
    return ['default-text', 'dict-key', 'popout-placement', 'show', 'button-type', 'icon-name', 'file-name', 'tooltip-default-text', 'tooltip-dict-key', 'tooltip-placement'];
  }

  constructor() {
    super();
    
    // Create shadow DOM with template content
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      ASPopoutButton.sheet
    ];
    this.render();
  }

  /**
   * Renders the component template with current state
   */
  private render(): void {
    this.shadowRoot!.innerHTML = /*html*/`
      ${this.renderButton()}
      
      <as-popout-card>
        <slot></slot>
      </as-popout-card>
    `;
  }

  /**
   * Renders the appropriate button based on button-type
   */
  private renderButton(): string {
    // Build tooltip attributes
    const tooltipAttrs = [];
    if (this.tooltipDefaultText) {
      tooltipAttrs.push(`tooltip-default-text="${this.tooltipDefaultText}"`);
    }
    if (this.tooltipDictKey) {
      tooltipAttrs.push(`tooltip-dict-key="${this.tooltipDictKey}"`);
    }
    if (this.tooltipPlacement) {
      tooltipAttrs.push(`tooltip-placement="${this.tooltipPlacement}"`);
    }
    const tooltipAttrString = tooltipAttrs.join(' ');

    if (this.buttonType === "icon") {
      // Render icon button
      const iconNameAttr = this.iconName ? `icon-name="${this.iconName}"` : '';
      const fileNameAttr = this.file_name ? `file-name="${this.file_name}"` : '';
      
      return `<as-icon-button ${iconNameAttr} ${fileNameAttr} ${tooltipAttrString}></as-icon-button>`;
    } else {
      // Render text button (default)
      const defaultTextAttr = this.textKey ? '' : `default-text="${this.buttonText}"`;
      const dictKeyAttr = this.textKey ? `dict-key="${this.textKey}"` : '';
      
      return `<as-text-button ${defaultTextAttr} ${dictKeyAttr} ${tooltipAttrString}></as-text-button>`;
    }
  }

  /**
   * Handles changes to observed attributes
   */
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'default-text':
        this.buttonText = newValue || 'Toggle';
        
        // If the component is already connected and using a text button, update the button text
        if (this.buttonElement && this.buttonType === "text" && !this.textKey) {
          this.buttonElement.setAttribute('default-text', this.buttonText);
        } else if (this.buttonType === "text" && !this.textKey) {
          // Otherwise re-render the component
          this.render();
        }
        break;
      case 'dict-key':
        this.textKey = newValue;
        
        // If the component is already connected and using a text button, update the text key
        if (this.buttonElement && this.buttonType === "text") {
          this.buttonElement.setAttribute('dict-key', this.textKey);
        } else if (this.buttonType === "text") {
          // Otherwise re-render the component
          this.render();
        }
        break;
      case 'button-type':
        this.buttonType = newValue || 'text';
        this.render();
        break;
      case 'icon-name':
        this.iconName = newValue;
        
        // If the component is already connected and using an icon button, update the icon
        if (this.buttonElement && this.buttonType === "icon") {
          this.buttonElement.setAttribute('icon-name', this.iconName);
        } else if (this.buttonType === "icon") {
          // Otherwise re-render the component
          this.render();
        }
        break;
      case 'file-name':
        this.file_name = newValue;
        
        // If the component is already connected and using an icon button, update the file name
        if (this.buttonElement && this.buttonType === "icon") {
          this.buttonElement.setAttribute('file-name', this.file_name);
        } else if (this.buttonType === "icon") {
          // Otherwise re-render the component
          this.render();
        }
        break;
      case 'tooltip-default-text':
        this.tooltipDefaultText = newValue;
        
        // If the component is already connected, update the tooltip
        if (this.buttonElement) {
          if (this.tooltipDefaultText) {
            this.buttonElement.setAttribute('tooltip-default-text', this.tooltipDefaultText);
          } else {
            this.buttonElement.removeAttribute('tooltip-default-text');
          }
        } else {
          // Otherwise re-render the component
          this.render();
        }
        break;
      case 'tooltip-dict-key':
        this.tooltipDictKey = newValue;
        
        // If the component is already connected, update the tooltip
        if (this.buttonElement) {
          if (this.tooltipDictKey) {
            this.buttonElement.setAttribute('tooltip-dict-key', this.tooltipDictKey);
          } else {
            this.buttonElement.removeAttribute('tooltip-dict-key');
          }
        } else {
          // Otherwise re-render the component
          this.render();
        }
        break;
      case 'tooltip-placement':
        this.tooltipPlacement = newValue || 'bottom';
        
        // If the component is already connected, update the tooltip placement
        if (this.buttonElement) {
          this.buttonElement.setAttribute('tooltip-placement', this.tooltipPlacement);
        } else {
          // Otherwise re-render the component
          this.render();
        }
        break;
      case 'popout-placement':
        // Validate placement value
        const validPlacements = ['right', 'left', 'top', 'bottom', 'bottom-left', 'bottom-right', 'top-left', 'top-right'];
        this.placement = validPlacements.includes(newValue) ? newValue : 'right';
        
        // Update the card's placement attribute
        if (this.popoutCard) {
          this.popoutCard.setAttribute('placement', this.placement);
        }
        break;
      case 'show':
        // Handle the show attribute - just pass it to the card
        if (this.popoutCard) {
          if (this.hasAttribute('show')) {
            this.popoutCard.setAttribute('show', '');
          } else {
            this.popoutCard.removeAttribute('show');
          }
        }
        break;
    }
  }

  /**
   * Finds the associated popout card when the component is connected to the DOM
   * and sets up event listeners
   */
  connectedCallback() {
    // Get the popout card element from shadow DOM
    this.popoutCard = this.shadowRoot!.querySelector('as-popout-card');
    
    // Get the button element and add event listener
    this.buttonElement = this.shadowRoot!.querySelector(this.buttonType === "icon" ? 'as-icon-button' : 'as-text-button');
    this.buttonElement?.addEventListener('as-button-click', this.handleClick);
    
    // Set initial text attribute if it exists and using text button
    if (this.hasAttribute('default-text') && this.buttonType === "text" && !this.textKey) {
      const textAttr = this.getAttribute('default-text');
      if (textAttr && this.buttonElement) {
        this.buttonText = textAttr;
        this.buttonElement.setAttribute('default-text', textAttr);
      }
    }
    
    // Set initial dict-key attribute if it exists and using text button
    if (this.hasAttribute('dict-key') && this.buttonType === "text") {
      const textKeyAttr = this.getAttribute('dict-key');
      if (textKeyAttr && this.buttonElement) {
        this.buttonElement.setAttribute('dict-key', textKeyAttr);
      }
    }
    
    // Set initial icon-name attribute if it exists and using icon button
    if (this.hasAttribute('icon-name') && this.buttonType === "icon") {
      const iconNameAttr = this.getAttribute('icon-name');
      if (iconNameAttr && this.buttonElement) {
        this.buttonElement.setAttribute('icon-name', iconNameAttr);
      }
    }
    
    // Set initial file-name attribute if it exists and using icon button
    if (this.hasAttribute('file-name') && this.buttonType === "icon") {
      const fileNameAttr = this.getAttribute('file-name');
      if (fileNameAttr && this.buttonElement) {
        this.buttonElement.setAttribute('file-name', fileNameAttr);
      }
    }
    
    // Set initial tooltip attributes
    if (this.hasAttribute('tooltip-default-text')) {
      const tooltipDefaultTextAttr = this.getAttribute('tooltip-default-text');
      if (tooltipDefaultTextAttr && this.buttonElement) {
        this.tooltipDefaultText = tooltipDefaultTextAttr;
        this.buttonElement.setAttribute('tooltip-default-text', tooltipDefaultTextAttr);
      }
    }
    
    if (this.hasAttribute('tooltip-dict-key')) {
      const tooltipDictKeyAttr = this.getAttribute('tooltip-dict-key');
      if (tooltipDictKeyAttr && this.buttonElement) {
        this.tooltipDictKey = tooltipDictKeyAttr;
        this.buttonElement.setAttribute('tooltip-dict-key', tooltipDictKeyAttr);
      }
    }
    
    if (this.hasAttribute('tooltip-placement')) {
      const tooltipPlacementAttr = this.getAttribute('tooltip-placement');
      if (tooltipPlacementAttr && this.buttonElement) {
        this.tooltipPlacement = tooltipPlacementAttr;
        this.buttonElement.setAttribute('tooltip-placement', tooltipPlacementAttr);
      }
    }
    
    // Set initial placement attribute if it exists
    if (this.hasAttribute('popout-placement')) {
      const placementAttr = this.getAttribute('popout-placement');
      if (placementAttr) {
        const validPlacements = ['right', 'left', 'top', 'bottom', 'bottom-left', 'bottom-right', 'top-left', 'top-right'];
        this.placement = validPlacements.includes(placementAttr) ? placementAttr : 'right';
        
        // Set the card's placement attribute
        if (this.popoutCard) {
          this.popoutCard.setAttribute('placement', this.placement);
        }
      }
    }
    
    // Set initial show attribute if it exists
    if (this.hasAttribute('show') && this.popoutCard) {
      this.popoutCard.setAttribute('show', '');
    }
    
    // Listen for events from the popout card
    if (this.popoutCard) {
      this.popoutCard.addEventListener('visibility-changed', this.handleVisibilityChanged);
      
      // Add click listeners to prevent closing when clicking the card
      const popoutCardElement = this.popoutCard as HTMLElement;
      popoutCardElement.addEventListener('mousedown', this.handlePopoutCardClick, true);
      popoutCardElement.addEventListener('click', this.handlePopoutCardClick, true);
      
      // Use a MutationObserver to observe the slotted content
      const slot = popoutCardElement.querySelector('slot');
      if (slot) {
        // Track assigned elements to clean up listeners properly
        let previousElements: Element[] = [];
        
        slot.addEventListener('slotchange', () => {
          const assignedElements = (slot as HTMLSlotElement).assignedElements();
          
          // Remove listeners from previous elements that are no longer assigned
          previousElements.forEach(el => {
            if (!assignedElements.includes(el)) {
              el.removeEventListener('mousedown', this.handlePopoutCardClick, true);
              el.removeEventListener('click', this.handlePopoutCardClick, true);
            }
          });
          
          // Add listeners to newly assigned elements
          assignedElements.forEach(el => {
            // First remove any existing listeners to prevent duplicates
            el.removeEventListener('mousedown', this.handlePopoutCardClick, true);
            el.removeEventListener('click', this.handlePopoutCardClick, true);
            
            // Add listeners to capture phase to ensure propagation is stopped early
            el.addEventListener('mousedown', this.handlePopoutCardClick, true);
            el.addEventListener('click', this.handlePopoutCardClick, true);
          });
          
          // Update the previous elements reference
          previousElements = [...assignedElements];
        });
        
        // Trigger slotchange manually to handle initial elements
        if ('slotchange' in HTMLSlotElement.prototype) {
          slot.dispatchEvent(new Event('slotchange'));
        } else {
          // Fallback for browsers without native slotchange event
          const assignedElements = (slot as HTMLSlotElement).assignedElements();
          assignedElements.forEach(el => {
            el.addEventListener('mousedown', this.handlePopoutCardClick, true);
            el.addEventListener('click', this.handlePopoutCardClick, true);
          });
          previousElements = [...assignedElements];
        }
      }
    }
    
    // Get button dimensions for positioning
    const buttonWidth = this.buttonElement!.offsetWidth;  
    const buttonHeight = this.buttonElement!.offsetHeight;

    this.style.setProperty('--button-width', `${buttonWidth}px`);
    this.style.setProperty('--button-height', `${buttonHeight}px`);
    
    // Update the card's CSS variables with button dimensions
    if (this.popoutCard) {
      (this.popoutCard as HTMLElement).style.setProperty('--reference-width', `${buttonWidth}px`);
      (this.popoutCard as HTMLElement).style.setProperty('--reference-height', `${buttonHeight}px`);
    }
  }
  
  /**
   * Handles the visibility-changed event from the popout card
   */
  private handleVisibilityChanged = (event: CustomEvent) => {
    // Update the button's show attribute to match the card
    if (event.detail.visible) {
      this.setAttribute('show', '');
      // Disable tooltips when popout is open
      if (this.buttonElement) {
        this.buttonElement.setAttribute('disable-tooltips', '');
      }
    } else {
      this.removeAttribute('show');
      // Re-enable tooltips when popout is closed
      if (this.buttonElement) {
        this.buttonElement.removeAttribute('disable-tooltips');
      }
    }
  }
  
  /**
   * Handles click events on the popout card to prevent bubbling
   * This stops clicks within the card from closing the popout
   */
  private handlePopoutCardClick = (event: Event) => {
    // Don't stop propagation or prevent default - let the event bubble naturally
    // Just mark the event as handled so we can check it in the outside click handler
    (event as any)._handledByPopoutCard = true;
    
    devlog('Marked event from popout card or slotted content as handled');
    
    // Return true to allow the event to continue for any needed internal handling
    return true;
  }
  
  /**
   * Handles click events on the button and dispatches the toggle-visibility event
   */
  private handleClick = (event: Event) => {
    // Prevent default button behavior
    event.preventDefault();
    devlog('Button clicked');
    
    // Update button dimensions before toggling
    if (this.buttonElement && this.popoutCard) {
      const buttonWidth = this.buttonElement.offsetWidth;
      const buttonHeight = this.buttonElement.offsetHeight;
      
      // Update the card's CSS variables with button dimensions
      (this.popoutCard as HTMLElement).style.setProperty('--reference-width', `${buttonWidth}px`);
      (this.popoutCard as HTMLElement).style.setProperty('--reference-height', `${buttonHeight}px`);
    }
    
    // Dispatch the toggle-visibility event
    if (this.popoutCard) {
      devlog("Dispatching toggle-visibility event");
      
      // Simply dispatch the toggle-visibility event
      this.popoutCard.dispatchEvent(new CustomEvent('toggle-visibility', {
        bubbles: true,
        composed: true
      }));
    } else devwarn("No popout card found");
  }

  /**
   * Clean up event listeners when the element is removed from the DOM
   */
  disconnectedCallback() {
    this.buttonElement?.removeEventListener('as-button-click', this.handleClick);
    
    if (this.popoutCard) {
      const popoutCardElement = this.popoutCard as HTMLElement;
      
      // Remove event listeners
      this.popoutCard.removeEventListener('visibility-changed', this.handleVisibilityChanged);
      popoutCardElement.removeEventListener('mousedown', this.handlePopoutCardClick, true);
      popoutCardElement.removeEventListener('click', this.handlePopoutCardClick, true);
      
      // Remove event listeners from slotted elements
      const slot = popoutCardElement.querySelector('slot') as HTMLSlotElement;
      if (slot) {
        // Get all assigned elements and remove listeners
        const assignedElements = slot.assignedElements();
        assignedElements.forEach(el => {
          el.removeEventListener('mousedown', this.handlePopoutCardClick, true);
          el.removeEventListener('click', this.handlePopoutCardClick, true);
        });
      }
    }
  }
}

customElements.define('as-popout-button', ASPopoutButton);

